import { useState } from 'react';
import { Button, Drawer, Tag, Space, Typography } from 'antd';
import { FilterOutlined, CloseOutlined } from '@ant-design/icons';
import type {
	FilterDefinition,
	UseFilterStateReturn,
	SingleValueFilterState,
	MultiValueFilterState,
	RangeFilterState,
} from '../types';
import { FilterRenderer } from '../components/FilterRenderer';

const { Text } = Typography;

interface FilterOption {
	value: string;
	label: string;
}

interface FilterOptionGroup {
	label: string;
	options: FilterOption[];
}

interface ResolvedOptionsMap {
	[filterKey: string]: FilterOption[] | undefined;
}

interface ResolvedOptionGroupsMap {
	[filterKey: string]: FilterOptionGroup[] | undefined;
}

interface Props {
	definitions: FilterDefinition[];
	filterState: UseFilterStateReturn;
	resolvedOptions?: ResolvedOptionsMap;
	resolvedOptionGroups?: ResolvedOptionGroupsMap;
	onResetAll: () => void;
	/** Which filters to show as quick-access chips (always visible) */
	quickAccessFilters?: string[];
}

export const TabletFilterBar = ({
	definitions,
	filterState,
	resolvedOptions = {},
	resolvedOptionGroups = {},
	onResetAll,
	quickAccessFilters = [],
}: Props) => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [activeDrawerFilter, setActiveDrawerFilter] = useState<string | null>(
		null
	);

	const visibleFilters = definitions.filter(
		(def) =>
			def.contexts.includes('tablet') && filterState.isVisible(def.key)
	);

	const quickFilters = visibleFilters.filter((d) =>
		quickAccessFilters.includes(d.key)
	);
	const drawerFilters = visibleFilters;

	// Count active filters for badge
	const activeFilterCount = visibleFilters.filter((def) => {
		const state = filterState.state[def.key];
		if (!state) return false;
		if ('value' in state) {
			if (typeof state.value === 'string') return state.value !== '';
			if (Array.isArray(state.value)) return state.value.length > 0;
		}
		return false;
	}).length;

	const getFilterSummary = (def: FilterDefinition): string | null => {
		const state = filterState.state[def.key];
		if (!state) return null;

		switch (def.type) {
			case 'range': {
				const rs = state as RangeFilterState;
				if (
					rs.value[0] === rs.initialRange[0] &&
					rs.value[1] === rs.initialRange[1]
				) {
					return null;
				}
				return `${rs.value[0]} – ${rs.value[1]}`;
			}
			case 'radio':
			case 'search-input': {
				const ss = state as SingleValueFilterState;
				if (!ss.value) return null;
				// Try to find label from options
				const opts = resolvedOptions[def.key];
				const match = opts?.find((o) => o.value === ss.value);
				return match?.label || ss.value;
			}
			case 'checkbox':
			case 'select': {
				const ms = state as MultiValueFilterState;
				if (ms.value.length === 0) return null;
				return `${ms.value.length} selected`;
			}
		}
		return null;
	};

	return (
		<>
			{/* Top bar */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					padding: '8px 12px',
					background: '#FBFBFB',
					border: '1px solid #D9D9D9',
					borderRadius: 2,
					overflowX: 'auto',
				}}
			>
				{/* Filter toggle button */}
				<Button
					icon={<FilterOutlined />}
					onClick={() => {
						setActiveDrawerFilter(null);
						setDrawerOpen(true);
					}}
					type={activeFilterCount > 0 ? 'primary' : 'default'}
					size="small"
				>
					Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
				</Button>

				{/* Quick access filter chips */}
				<Space size={4} wrap={false} style={{ overflow: 'auto' }}>
					{quickFilters.map((def) => {
						const summary = getFilterSummary(def);
						return (
							<Tag
								key={def.key}
								style={{
									cursor: 'pointer',
									borderStyle: summary ? 'solid' : 'dashed',
								}}
								color={summary ? 'blue' : undefined}
								onClick={() => {
									setActiveDrawerFilter(def.key);
									setDrawerOpen(true);
								}}
							>
								{def.label}
								{summary ? `: ${summary}` : ''}
								{summary && (
									<CloseOutlined
										style={{ marginLeft: 4, fontSize: 10 }}
										onClick={(e) => {
											e.stopPropagation();
											filterState.resetFilter(def.key);
										}}
									/>
								)}
							</Tag>
						);
					})}
				</Space>

				{activeFilterCount > 0 && (
					<Button type="link" size="small" onClick={onResetAll}>
						Clear all
					</Button>
				)}
			</div>

			{/* Drawer for full filter editing */}
			<Drawer
				title="Filters"
				placement="bottom"
				height="70vh"
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				extra={
					<Button type="link" onClick={onResetAll}>
						Reset all
					</Button>
				}
			>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					{drawerFilters.map((def) => {
						const isActive = activeDrawerFilter === def.key;
						return (
							<div
								key={def.key}
								style={{
									padding: 12,
									border: '1px solid #d9d9d9',
									borderRadius: 8,
									background: isActive ? '#f0f5ff' : '#fff',
								}}
							>
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										marginBottom: 8,
									}}
								>
									<Text strong>{def.label}</Text>
									{def.resettable !== false && (
										<Button
											type="link"
											size="small"
											onClick={() => filterState.resetFilter(def.key)}
										>
											Reset
										</Button>
									)}
								</div>
								<FilterRenderer
									definition={def}
									filterState={filterState}
									resolvedOptions={resolvedOptions[def.key]}
									resolvedOptionGroups={resolvedOptionGroups[def.key]}
									compact={true}
								/>
							</div>
						);
					})}
				</div>
			</Drawer>
		</>
	);
};